
// Language utility functions for the application.
// All 11 selectable languages from translations/schema/languages.json. Order
// follows the schema: tier-1 first (en/es/fr/de/it), then tier-2 (pt/uk/ro),
// then tier-3 (ru/pl/nl). ar/zh stay deferred until product enables them.

export const LANGUAGES = [
  { code: 'en', name: 'English', nativeName: 'English', flag: '🇺🇸' },
  { code: 'es', name: 'Spanish', nativeName: 'Español', flag: '🇪🇸' },
  { code: 'fr', name: 'French', nativeName: 'Français', flag: '🇫🇷' },
  { code: 'de', name: 'German', nativeName: 'Deutsch', flag: '🇩🇪' },
  { code: 'it', name: 'Italian', nativeName: 'Italiano', flag: '🇮🇹' },
  { code: 'pt', name: 'Portuguese', nativeName: 'Português', flag: '🇵🇹' },
  { code: 'uk', name: 'Ukrainian', nativeName: 'Українська', flag: '🇺🇦' },
  { code: 'ro', name: 'Romanian', nativeName: 'Română', flag: '🇷🇴' },
  { code: 'ru', name: 'Russian', nativeName: 'Русский', flag: '🇷🇺' },
  { code: 'pl', name: 'Polish', nativeName: 'Polski', flag: '🇵🇱' },
  { code: 'nl', name: 'Dutch', nativeName: 'Nederlands', flag: '🇳🇱' },
];

export const SUPPORTED_LOCALE_CODES = LANGUAGES.map(l => l.code);

export const getLanguageName = (code: string): string => {
  const language = LANGUAGES.find(lang => lang.code === code);
  return language ? language.nativeName : 'English';
};

export const getLanguageByCode = (code: string) => {
  return LANGUAGES.find(lang => lang.code === code) || LANGUAGES[0];
};

/**
 * Resolve the best supported locale from a BCP-47 language tag.
 * e.g. "it-IT" → "it", "pt-BR" → "pt", "zh-CN" → undefined (deferred).
 */
export const resolveDeviceLocale = (navigatorLang: string): string | undefined => {
  if (!navigatorLang) return undefined;
  const base = navigatorLang.split('-')[0].toLowerCase();
  return SUPPORTED_LOCALE_CODES.includes(base) ? base : undefined;
};

/**
 * Resolve a profile-supplied language preference to a supported code, or undefined.
 * Mirrors the BCP-47 normalisation used by `resolveDeviceLocale` so a saved
 * profile value of "es-ES" still resolves to "es".
 */
export const resolveProfileLocale = (
  profileLanguage: string | null | undefined,
): string | undefined => {
  if (!profileLanguage || typeof profileLanguage !== 'string') return undefined;
  return resolveDeviceLocale(profileLanguage);
};

/**
 * Locale precedence resolver — pure, deterministic, testable.
 * Mirrors iOS `AppLocaleResolver`:
 *   1. Explicit override (selectedLanguage in localStorage)
 *   2. Saved profile language
 *   3. Device preferred language
 *   4. English fallback
 */
export interface LocalePrecedenceInputs {
  explicit?: string | null;
  profile?: string | null;
  device?: string | null;
}

export const resolveLocale = (inputs: LocalePrecedenceInputs): string => {
  const explicit = resolveProfileLocale(inputs.explicit ?? null);
  if (explicit) return explicit;
  const profile = resolveProfileLocale(inputs.profile ?? null);
  if (profile) return profile;
  const device = resolveDeviceLocale(inputs.device ?? '');
  if (device) return device;
  return 'en';
};
